import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const BASE_URL = (process.env.REACT_APP_AI_ASSISTANT_BASE_URL || "").replace(/\/$/, "")
const AI_TOKEN = process.env.REACT_APP_AI_ASSISTANT_TOKEN || ""
const aiHeaders = () => ({ "DS-Api-Token": AI_TOKEN })

// ACTIONS — open/close
const openRequested = createAction("chatCard/openRequested")
const closeRequested = createAction("chatCard/closeRequested")

// ACTIONS — processQuery
const processQueryRequested = createAction("aiAssistant/processQueryRequested")
const processQueryReceived = createAction("aiAssistant/processQueryReceived")
const processQueryFailed = createAction("aiAssistant/processQueryFailed")

// ACTIONS — fetchSessions
const fetchSessionsRequested = createAction("aiAssistant/fetchSessionsRequested")
const fetchSessionsReceived = createAction("aiAssistant/fetchSessionsReceived")
const fetchSessionsFailed = createAction("aiAssistant/fetchSessionsFailed")

// ACTIONS — fetchChatHistory
const fetchChatHistoryRequested = createAction("aiAssistant/fetchChatHistoryRequested")
const fetchChatHistoryReceived = createAction("aiAssistant/fetchChatHistoryReceived")
const fetchChatHistoryFailed = createAction("aiAssistant/fetchChatHistoryFailed")

// ACTIONS — parseReceipt
const parseReceiptRequested = createAction("aiAssistant/parseReceiptRequested")
const parseReceiptReceived = createAction("aiAssistant/parseReceiptReceived")
const parseReceiptFailed = createAction("aiAssistant/parseReceiptFailed")

// ACTIONS — deleteSession
const deleteSessionRequested = createAction("aiAssistant/deleteSessionRequested")
const deleteSessionReceived = createAction("aiAssistant/deleteSessionReceived")
const deleteSessionFailed = createAction("aiAssistant/deleteSessionFailed")

// ACTIONS — submitPaymentMethod
const submitPaymentMethodRequested = createAction("aiAssistant/submitPaymentMethodRequested")
const submitPaymentMethodReceived = createAction("aiAssistant/submitPaymentMethodReceived")
const submitPaymentMethodFailed = createAction("aiAssistant/submitPaymentMethodFailed")

// INITIAL STATE
const initialState = {
  isOpen: false,
  processQuery: { ...LOADING_INITIAL_STATE },
  sessions: { ...LOADING_INITIAL_STATE },
  chatHistory: { ...LOADING_INITIAL_STATE },
  parseReceipt: { ...LOADING_INITIAL_STATE },
  deleteSession: { ...LOADING_INITIAL_STATE },
  submitPaymentMethod: { ...LOADING_INITIAL_STATE }
}

// REDUCER
const chatCardReducer = createReducer(initialState, {
  [openRequested.type]: (state) => { state.isOpen = true },
  [closeRequested.type]: (state) => { state.isOpen = false },

  [processQueryRequested.type]: (state) => { setRequested(state.processQuery) },
  [processQueryReceived.type]: (state) => { setReceived(state.processQuery) },
  [processQueryFailed.type]: (state) => { setFailed(state.processQuery) },

  [fetchSessionsRequested.type]: (state) => { setRequested(state.sessions) },
  [fetchSessionsReceived.type]: (state) => { setReceived(state.sessions) },
  [fetchSessionsFailed.type]: (state) => { setFailed(state.sessions) },

  [fetchChatHistoryRequested.type]: (state) => { setRequested(state.chatHistory) },
  [fetchChatHistoryReceived.type]: (state) => { setReceived(state.chatHistory) },
  [fetchChatHistoryFailed.type]: (state) => { setFailed(state.chatHistory) },

  [parseReceiptRequested.type]: (state) => { setRequested(state.parseReceipt) },
  [parseReceiptReceived.type]: (state) => { setReceived(state.parseReceipt) },
  [parseReceiptFailed.type]: (state) => { setFailed(state.parseReceipt) },

  [deleteSessionRequested.type]: (state) => { setRequested(state.deleteSession) },
  [deleteSessionReceived.type]: (state) => { setReceived(state.deleteSession) },
  [deleteSessionFailed.type]: (state) => { setFailed(state.deleteSession) },

  [submitPaymentMethodRequested.type]: (state) => { setRequested(state.submitPaymentMethod) },
  [submitPaymentMethodReceived.type]: (state) => { setReceived(state.submitPaymentMethod) },
  [submitPaymentMethodFailed.type]: (state) => { setFailed(state.submitPaymentMethod) }
})

export default chatCardReducer

// PUBLIC ACTIONS
export const openChatCard = () => openRequested()
export const closeChatCard = () => closeRequested()

// THUNKS
export const parseReceipt = (file) => {
  const data = new FormData()
  data.append("file", file)
  return api({
    url: `${BASE_URL}/virtual-assistant/pros/parse-receipt`,
    method: "post",
    data,
    onStart: parseReceiptRequested.type,
    onSuccess: parseReceiptReceived.type,
    onError: parseReceiptFailed.type,
    suppressErrorNotification: true,
    extraHeaders: { ...aiHeaders(), "Content-Type": "multipart/form-data" }
  })
}

export const processQuery = (sessionId, query, receiptMetadata) => {
  const data = { session_id: sessionId, query }
  if (receiptMetadata) data.receipt_metadata = receiptMetadata
  return api({
    url: `${BASE_URL}/virtual-assistant/pros/process-query`,
    method: "post",
    data,
    onStart: processQueryRequested.type,
    onSuccess: processQueryReceived.type,
    onError: processQueryFailed.type,
    suppressErrorNotification: true,
    extraHeaders: aiHeaders()
  })
}

export const fetchChatSessions = () =>
  api({
    url: `${BASE_URL}/virtual-assistant/pros/chat-sessions`,
    method: "get",
    onStart: fetchSessionsRequested.type,
    onSuccess: fetchSessionsReceived.type,
    onError: fetchSessionsFailed.type,
    suppressErrorNotification: true,
    extraHeaders: aiHeaders()
  })

export const fetchChatHistory = (sessionId) =>
  api({
    url: `${BASE_URL}/virtual-assistant/pros/chat-history/${sessionId}`,
    method: "get",
    onStart: fetchChatHistoryRequested.type,
    onSuccess: fetchChatHistoryReceived.type,
    onError: fetchChatHistoryFailed.type,
    suppressErrorNotification: true,
    extraHeaders: aiHeaders()
  })

export const deleteChatSession = (sessionId) =>
  api({
    url: `${BASE_URL}/virtual-assistant/pros/chat-history/${sessionId}`,
    method: "delete",
    onStart: deleteSessionRequested.type,
    onSuccess: deleteSessionReceived.type,
    onError: deleteSessionFailed.type,
    suppressErrorNotification: true,
    extraHeaders: aiHeaders()
  })

export const submitPaymentMethod = (addCard, paymentMethodId) => {
  const { session_id, client_user_id, amount } = addCard || {}
  return api({
    url: `${BASE_URL}/virtual-assistant/pros/ai-sessions/${session_id}/payment-method`,
    method: "post",
    data: { payment_method_id: paymentMethodId, client_user_id, amount },
    onStart: submitPaymentMethodRequested.type,
    onSuccess: submitPaymentMethodReceived.type,
    onError: submitPaymentMethodFailed.type,
    suppressErrorNotification: true,
    extraHeaders: aiHeaders()
  })
}
